// Shows a one-time, mobile-only modal nudging the native-app download on a web online app.
//
// Gating (all must hold):
//   - the app opted in server-side (`onlineApp.showAppDownloadPrompt`, from the YAML flag)
//   - the device is a mobile browser (client-side; `requestContext.isMobileBrowser()`)
//   - we are NOT inside the native app WebView (`requestContext.isNativeApp()`)
//   - it has not already been dismissed on this device (localStorage; see `Dismissal`)
//
// Content is rendered server-side (hidden) into the page so the modal can show early — before
// markers/resources finish loading — without an extra fetch. We reuse the existing Modalbox
// singleton (async-loaded via `_js_components`), polling briefly for it to appear.

import { requestContext } from "../../lib/request_context";
import { getGlobalComponentOfType } from "../../lib/global_components";
import { ModalboxConfig } from "../../components/modalbox/config";
import Dismissal from "./dismissal";

const CONTENT_SELECTOR = '.online_app__app_download_prompt_content';
const MODALBOX_POLL_INTERVAL_MS = 250;
const MODALBOX_POLL_MAX_ATTEMPTS = 40; // ~10s — the Modalbox bundle loads async on DOMready.

export default class AppDownloadPrompt {

    constructor(onlineApp) {
        this.onlineApp = onlineApp;
        this.dismissal = new Dismissal(onlineApp.code);
    }

    // Show the prompt if all gating conditions are met. Safe to call unconditionally on boot.
    maybeShow() {
        if (!this.shouldShow()) { return; }
        this.waitForModalbox()
            .then(modalbox => this.show(modalbox))
            .catch(() => { /* Modalbox never appeared — silently skip, the footer promo remains. */ });
    }

    shouldShow() {
        return !!this.onlineApp.showAppDownloadPrompt
            && requestContext.isMobileBrowser()
            && !requestContext.isNativeApp()
            && !this.dismissal.isDismissed();
    }

    show(modalbox) {
        const content = this.getContent();
        if (!content) { return; }

        // Record the dismissal up front so it shows once ever, even if the visitor reloads or
        // navigates away without explicitly closing it.
        this.dismissal.markDismissed();

        modalbox.open({ calling_el: this.onlineApp.getRootElement() });
        modalbox.update(content);
    }

    getContent() {
        const element = document.querySelector(CONTENT_SELECTOR);
        return element ? element.innerHTML : null;
    }

    getModalbox() {
        return getGlobalComponentOfType(ModalboxConfig.componentTypeKey);
    }

    // The Modalbox bundle loads asynchronously, so it may not exist yet when the app boots.
    // Poll for it for a short while.
    waitForModalbox() {
        return new Promise((resolve, reject) => {
            let attempts = 0;
            const poll = () => {
                const modalbox = this.getModalbox();
                if (modalbox) { return resolve(modalbox); }
                if (++attempts >= MODALBOX_POLL_MAX_ATTEMPTS) { return reject(); }
                window.setTimeout(poll, MODALBOX_POLL_INTERVAL_MS);
            };
            poll();
        });
    }

}
